import axios, { AxiosInstance } from 'axios';
import { format, isValid, parseISO } from 'date-fns';
import { apiConfig } from '@/config/apiConfig';
import { parseDateOnly } from '@/lib/utils';
import { getVoiceFallbackClient } from './_shared';

// ── Voice campaigns (/voice-campaigns) ───────────────────────────────

export type VoiceCampaignRow = {
  id: string;
  name: string;
  type: string;
  icon: string;
  bgColor: string;
  status: string;
  sessions: number;
  scans: number;
  recipients: number;
  bookRate: string;
  costPerAppt: string;
  tags: string[];
  dropDate: string;
  endDate: string;
  raw?: any;
  uploaded_filename?: string;
  /** Frontend Integration F0.1 — the 13-value campaign_type taxonomy (models/campaign_type.py); null on legacy/untyped campaigns. */
  campaign_type?: string | null;
  /** Per-campaign_type trigger config (models/trigger_config.py); shape varies by campaign_type. */
  trigger_config?: Record<string, unknown> | null;
  consent_basis?: string | null;
  consent_attestation?: Record<string, unknown> | null;
  state_exclusions?: string[];
  agent_greeting?: string | null;
  /** Greeting override/inheritance (Gap Remediation) — true once this campaign's agent_greeting was explicitly set via PATCH, protecting it from the linked Agent's greeting propagating over it. */
  greeting_overridden?: boolean;
};

/**
 * Maps raw API response from /api/campaigns to the VoiceCampaignRow format used in the UI.
 */
export function mapVoiceCampaign(data: any): VoiceCampaignRow {
  const verticalMap: Record<string, { label: string; icon: string; color: string }> = {
    home_services: { label: 'Home Services', icon: 'solar:home-2-bold', color: '#FEF3C7' },
    healthcare: { label: 'Healthcare', icon: 'solar:hospital-bold', color: '#ECFDF5' },
    financial: { label: 'Financial', icon: 'solar:chart-square-bold', color: '#EEF3FF' },
    automotive: { label: 'Automotive', icon: 'gravity-ui:car', color: '#F0FDF4' },
    real_estate: { label: 'Real Estate', icon: 'solar:home-smile-bold', color: '#FFFBEB' },
  };

  const vInfo = verticalMap[data.vertical as string] || { label: data.vertical || 'Any Industry', icon: 'solar:settings-bold', color: '#F1F5F9' };

  return {
    id: data.id,
    name: data.name || 'Untitled Campaign',
    type: `${vInfo.label} · ${data.volume ? `${(data.volume / 1000).toFixed(0)}k` : '0k'} mailer drop`,
    icon: vInfo.icon,
    bgColor: vInfo.color,
    status: data.status || 'draft',
    sessions: Number(data.sessions ?? data.session_count ?? data.total_sessions ?? data.voice_sessions ?? 0),
    scans: Number(data.scans ?? data.scan_count ?? data.total_scans ?? 0),
    recipients: Number(data.total_recipients ?? data.recipients ?? data.recipient_count ?? 0),
    bookRate: (data as any).bookRate || '—',
    costPerAppt: (data as any).costPerAppt || '—',
    tags: (data.tools_enabled || []).slice(0, 3),
    dropDate: data.drop_date ? (parseDateOnly(data.drop_date) ?? new Date()).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '—',
    endDate: data.end_date ? (parseDateOnly(data.end_date) ?? new Date()).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '—',
    raw: data,
    campaign_type: data.campaign_type ?? null,
    trigger_config: data.trigger_config ?? null,
    consent_basis: data.consent_basis ?? null,
    consent_attestation: data.consent_attestation ?? null,
    state_exclusions: Array.isArray(data.state_exclusions) ? data.state_exclusions : [],
    agent_greeting: data.agent_greeting ?? null,
    greeting_overridden: Boolean(data.greeting_overridden),
    // Session/scan/recipient counts are real call activity, not proof a CSV was
    // ever uploaded — only audience_count (set exclusively by the audience-upload
    // endpoint) means an audience file actually exists for this campaign.
    uploaded_filename:
      data.uploaded_filename ??
      data.raw?.uploaded_filename ??
      (Number(data.audience_count ?? data.final_audience_count ?? data.total_uploaded ?? 0) > 0
        ? 'Imported Audience'
        : undefined),
  };
}

export async function fetchVoiceCampaigns(axiosInstance?: AxiosInstance): Promise<VoiceCampaignRow[]> {
  const client = axiosInstance || getVoiceFallbackClient();
  const url = apiConfig.voiceEndpoints.campaigns;
  const response = await client.get<any[]>(url);
  return (response.data || []).map(mapVoiceCampaign);
}

export type CreateVoiceCampaignInput = { name?: string; type?: string };

export async function createVoiceCampaign(input: CreateVoiceCampaignInput = {}, axiosInstance?: AxiosInstance): Promise<VoiceCampaignRow> {
  const client = axiosInstance || getVoiceFallbackClient();
  const url = apiConfig.voiceEndpoints.campaigns;
  const response = await client.post<VoiceCampaignRow>(url, input);
  return response.data;
}

export async function updateVoiceCampaign(id: string, patch: Partial<Omit<VoiceCampaignRow, 'id'>>, axiosInstance?: AxiosInstance): Promise<VoiceCampaignRow | null> {
  const client = axiosInstance || getVoiceFallbackClient();
  const url = `${apiConfig.voiceEndpoints.campaigns}/${id}`;
  const response = await client.put<VoiceCampaignRow>(url, patch);
  return response.data;
}

/**
 * Greeting override/inheritance (Gap Remediation) — "Reset to Agent": drops this
 * campaign's own greeting override and re-pulls the linked Agent's current greeting.
 * `PUT /api/campaigns/{id}` with `reset_greeting_to_agent: true`.
 */
export async function resetCampaignGreetingToAgent(
  id: string, axiosInstance?: AxiosInstance,
): Promise<{ agent_greeting: string | null; greeting_overridden: boolean }> {
  const client = axiosInstance || getVoiceFallbackClient();
  const url = `${apiConfig.voiceEndpoints.campaigns}/${id}`;
  const response = await client.put(url, { reset_greeting_to_agent: true });
  return {
    agent_greeting: response.data?.agent_greeting ?? null,
    greeting_overridden: Boolean(response.data?.greeting_overridden),
  };
}

/**
 * Re-assign this campaign's voice agent. `PUT /api/campaigns/{id}` with
 * `agent_id` — the backend copies `elevenlabs_agent_id` onto the campaign
 * and reprovisions its tools (routes/campaigns.py).
 */
export async function assignVoiceCampaignAgent(
  id: string, agentId: string, axiosInstance?: AxiosInstance,
): Promise<{ agent_id: string | null }> {
  const client = axiosInstance || getVoiceFallbackClient();
  const url = `${apiConfig.voiceEndpoints.campaigns}/${id}`;
  const response = await client.put(url, { agent_id: agentId });
  return {
    agent_id: response.data?.agent_id ?? null,
  };
}

export async function deleteVoiceCampaign(id: string, axiosInstance?: AxiosInstance): Promise<boolean> {
  const client = axiosInstance || getVoiceFallbackClient();
  const url = `${apiConfig.voiceEndpoints.campaigns}/${id}`;
  const response = await client.delete(url);
  return response.status === 204 || response.status === 200;
}

/** `POST /api/campaigns/bulk-delete` — one round trip instead of N individual DELETEs. */
export async function bulkDeleteVoiceCampaigns(
  ids: string[], axiosInstance?: AxiosInstance
): Promise<{ deleted: number; failed: Array<{ campaign_id: string; error: string }> }> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.post(`${apiConfig.voiceEndpoints.campaigns}/bulk-delete`, { campaign_ids: ids });
  return response.data;
}

/** Builder “Launch” — adds a new live campaign (mock). */
export async function launchVoiceCampaign(input: { name?: string } = {}, axiosInstance?: AxiosInstance): Promise<VoiceCampaignRow> {
  const client = axiosInstance || getVoiceFallbackClient();
  const url = apiConfig.voiceEndpoints.campaigns;
  const response = await client.post<VoiceCampaignRow>(url, { ...input, status: 'live' });
  return response.data;
}
